import { polyfill } from 'es6-promise';
import axios from 'axios';
import { isNil, omitBy } from 'lodash';
import authProvider from '@/auth/authProvider';
import { isCommonsModeEnabled } from '@/auth/featureToggles';

polyfill();

// Set common axios properties
// These can be overridden if necessary
export const client = axios.create({
  baseURL: '/api/',
  headers: {
    Pragma: 'no-cache',
    'Cache-Control': 'no-cache',
    Expires: -1,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const MAX_401_RETRIES = 10;
let handle401retries = 0;

const addAuthorizationHeader = (idToken, headers) => {
  if (idToken) {
    return {
      ...headers,
      Authorization: `Bearer ${idToken}`,
    };
  }

  return headers;
};

const handle401Failure = async (error) => {
  const {
    // Original request that failed
    config: request,
  } = error;

  handle401retries += 1;

  try {
    if (handle401retries > MAX_401_RETRIES) {
      throw new Error('Max retries reached on 401 handler ');
    }
    const user = await authProvider().reauthenticateUser();

    request.headers = addAuthorizationHeader(user.id_token, request.headers);

    return client(request);
  } catch (reauthenticateError) {
    window.console.error({ reauthenticateError });
    return Promise.reject(error);
  }
};

const responseOnFulfilled = (response) => {
  handle401retries = 0;

  return response;
};

const responseOnRejected = async (error) => {
  const { response } = error;

  if (response && response.status === 401) {
    const isAuthenticated = await authProvider().getIsAuthenticated();
    const shouldHandle401 = isAuthenticated || !isCommonsModeEnabled();

    if (shouldHandle401) {
      return handle401Failure(error);
    }
  }

  return Promise.reject(error);
};

client.interceptors.response.use(responseOnFulfilled, responseOnRejected);

const request = async function request(options) {
  const axiosOptions = options;
  const isDevelopment = process.env.NODE_ENV === 'development';

  const onSuccess = function onSuccess(response) {
    if (isDevelopment) {
      window.console.debug('Request Successful!', response);
    }
    return response.data;
  };

  const onError = function onError(error) {
    if (isDevelopment) {
      window.console.error('Request Failed:', error.config);
    }

    if (error.response) {
      // Request was made but server responded with something
      // other than 2xx
      if (isDevelopment) {
        window.console.error('Status:', error.response.status);
        window.console.error('Data:', error.response.data);
        window.console.error('Headers:', error.response.headers);
      }
    } else {
      // Something else happened while setting up the request
      // trigger the error

      // eslint-disable-next-line no-lonely-if
      if (isDevelopment) {
        window.console.error('Error Message:', error.message);
      }
    }

    return Promise.reject(error.response || error.message);
  };

  const idToken = await authProvider().getIdToken();

  axiosOptions.headers = addAuthorizationHeader(idToken, axiosOptions.headers);

  return client({
    ...axiosOptions,
    // Do not send null/undefined query params
    params: omitBy(axiosOptions.params, isNil),
  }).then(onSuccess).catch(onError);
};

export default request;
